import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Sparkles, Wallet, MapPin, Building2, Sun, Waves,
  Mail, Phone, ArrowRight, PartyPopper, Store,
} from "lucide-react";

const WHY = [
  { icon: ShieldCheck, title: "Vetted quality", text: "We personally vet every vendor on our platform, ensuring only the most skilled and reliable professionals handle your special moments." },
  { icon: Sparkles, title: "Excellence in every detail", text: "From grand temple festivals to intimate beach weddings, we maintain a standard of service that prioritises perfection in every detail." },
  { icon: Wallet, title: "Cost effective", text: "Transparent, fixed-price packages and direct vendor quoting — premium service that fits your budget, with no hidden costs." },
];

const CLIENTS = [
  { name: "Global Tech Corp", icon: Building2 },
  { name: "Sunshine Resorts", icon: Sun },
  { name: "Oceanic Events", icon: Waves },
];

const LOCATIONS = ["Kundapura", "Udupi", "Byndoor"];

export default function Footer() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill your name, email and message");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! Our team will reach out to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-stone-900 text-stone-300 mt-16">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-14">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* About + Why */}
          <div className="lg:col-span-7 space-y-10">
            <div data-testid="footer-about">
              <div className="inline-flex items-center gap-2 text-primary font-bold uppercase tracking-[0.2em] text-xs mb-3">
                <PartyPopper className="h-4 w-4" /> About us
              </div>
              <h3 className="font-display font-extrabold text-3xl text-white leading-tight">
                The Udupi region's premier event marketplace
              </h3>
              <p className="text-stone-400 mt-3 max-w-xl leading-relaxed">
                Booking an event shouldn't be a gamble. Book My Event fixes unreliable, hit-or-miss event
                services by giving customers a <span className="text-stone-200 font-medium">carefully vetted marketplace</span> of
                trusted decorators, DJs, caterers and photographers across coastal Karnataka — with transparent
                pricing and no surprises. At the same time, we give <span className="text-stone-200 font-medium">local vendors a professional
                platform</span> to showcase their best work, reach more customers and manage bookings effortlessly.
              </p>
            </div>

            <div data-testid="footer-why">
              <h4 className="font-display font-bold text-lg text-white mb-4">Why Book My Event</h4>
              <div className="grid sm:grid-cols-3 gap-4">
                {WHY.map((w) => (
                  <div key={w.title} className="rounded-2xl bg-stone-800/60 border border-stone-700/60 p-4">
                    <div className="h-10 w-10 rounded-xl bg-primary/20 text-primary grid place-items-center mb-3">
                      <w.icon className="h-5 w-5" />
                    </div>
                    <p className="font-semibold text-white text-sm">{w.title}</p>
                    <p className="text-xs text-stone-400 mt-1 leading-relaxed">{w.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div data-testid="footer-clients">
              <h4 className="font-display font-bold text-lg text-white mb-4">Trusted by top clients</h4>
              <div className="flex flex-wrap gap-3">
                {CLIENTS.map((c) => (
                  <div
                    key={c.name}
                    data-testid={`client-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                    className="flex items-center gap-2.5 rounded-xl bg-stone-800/60 border border-stone-700/60 px-4 py-3"
                  >
                    <c.icon className="h-6 w-6 text-secondary" />
                    <span className="font-display font-bold text-white text-sm tracking-tight">{c.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-5">
            <div data-testid="footer-contact" className="rounded-2xl bg-stone-800/60 border border-stone-700/60 p-5">
              <h4 className="font-display font-bold text-lg text-white">Contact us</h4>
              <p className="text-sm text-stone-400 mt-1 mb-4">Planning something? Send us a note and we'll help you build it.</p>
              <form onSubmit={submit} className="grid gap-3">
                <input
                  data-testid="footer-contact-name"
                  placeholder="Name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="h-11 rounded-xl bg-stone-900 border border-stone-700 px-4 text-sm text-white placeholder:text-stone-500 outline-none focus:border-primary transition"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    data-testid="footer-contact-email"
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="h-11 rounded-xl bg-stone-900 border border-stone-700 px-4 text-sm text-white placeholder:text-stone-500 outline-none focus:border-primary transition"
                  />
                  <input
                    data-testid="footer-contact-phone"
                    placeholder="Phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="h-11 rounded-xl bg-stone-900 border border-stone-700 px-4 text-sm text-white placeholder:text-stone-500 outline-none focus:border-primary transition"
                  />
                </div>
                <textarea
                  data-testid="footer-contact-message"
                  placeholder="Tell us about your event..."
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="rounded-xl bg-stone-900 border border-stone-700 px-4 py-3 text-sm text-white placeholder:text-stone-500 outline-none focus:border-primary transition resize-none"
                />
                <button
                  data-testid="footer-contact-submit"
                  type="submit"
                  disabled={sending}
                  className="h-11 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
                >
                  {sending ? "Sending..." : "Send enquiry"}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Locations + Careers + bottom bar */}
        <div className="border-t border-stone-800 mt-12 pt-8 grid gap-8 md:grid-cols-3">
          <div data-testid="footer-locations">
            <h4 className="font-display font-bold text-white mb-3">Locations we service</h4>
            <ul className="space-y-2">
              {LOCATIONS.map((l) => (
                <li key={l} className="flex items-center gap-2 text-sm text-stone-400">
                  <MapPin className="h-4 w-4 text-primary" /> {l}
                </li>
              ))}
            </ul>
          </div>

          <div data-testid="footer-careers-block">
            <h4 className="font-display font-bold text-white mb-3">Join us</h4>
            <Link
              to="/become-vendor"
              data-testid="footer-become-vendor-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-secondary hover:text-white transition-colors"
            >
              List your business on Book My Event <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/vendor/login"
              data-testid="footer-vendor-login-link"
              className="mt-3 flex items-center gap-2 text-sm font-semibold text-stone-300 hover:text-white transition-colors"
            >
              <Store className="h-4 w-4 text-primary" /> Vendor portal login
            </Link>
            <Link
              to="/careers"
              data-testid="footer-careers-link"
              className="mt-3 flex items-center gap-2 text-sm font-semibold text-stone-300 hover:text-white transition-colors"
            >
              Career opportunities <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/orders"
              data-testid="footer-orders-link"
              className="mt-3 flex items-center gap-2 text-sm font-semibold text-stone-300 hover:text-white transition-colors"
            >
              Track my bookings <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div>
            <h4 className="font-display font-bold text-white mb-3">Reach us</h4>
            <ul className="space-y-2 text-sm text-stone-400">
              <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> bookmyevent.bme@gmail.com</li>
              <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> +91 90000 00000</li>
            </ul>
          </div>
        </div>

        <p className="text-xs text-stone-500 mt-10">
          © {new Date().getFullYear()} Book My Event · Kundapura, Udupi District, Karnataka · Tap. Book. Celebrate.
        </p>
      </div>
    </footer>
  );
}
